import { LotKindSchema, PlacementCollectSchema } from '@neon-one/auctions-types';
import type { PlacementCollect, PlacementOpenInvoice, ReassignTargetConsequence } from '@neon-one/auctions-types';

import { countLabel, formatCents, type LotNoun } from './format';
import { releaseRefunds } from './payment-status';
import { ReassignMode, type ReassignModeValue } from './item-actions';
import type { PaymentStatus } from './types';
import type { ReassignSubject } from './reassign-target';

// The server's two answers to "where would this lot land?", named so no surface
// compares against a bare string. Same members as the wire's
// `ReassignTargetConsequence` — this is that vocabulary, not a second one.
export const TargetLanding = {
    Appends: 'appends',
    NewInvoice: 'new_invoice',
} as const satisfies Record<string, ReassignTargetConsequence>;
export type TargetLandingValue = (typeof TargetLanding)[keyof typeof TargetLanding];

// The product's offer window, and the wording of the mode card that promises
// it. `offerWindowLabel` prefers the deadline the server actually returned;
// this is what stands in before one exists.
export const OFFER_WINDOW_LABEL = '24 hours';


// ── What confirming does to the lot's current home ───────────────────────

export const ConsequenceKind = {
    EndsOffer: 'ends_offer',
    RefundAndRelease: 'refund_and_release',
    RefundWhenSettled: 'refund_when_settled',
    ReleaseOnly: 'release_only',
    Placement: 'placement',
    Collect: 'collect',
} as const;
export type ConsequenceKindValue = (typeof ConsequenceKind)[keyof typeof ConsequenceKind];

const MessageSeverity = { Warn: 'warn', Info: 'info' } as const;
const CollectChoice = PlacementCollectSchema.enum;
const LotKind = LotKindSchema.enum;

export interface ConsequenceMessage {
    kind: ConsequenceKindValue;
    severity: (typeof MessageSeverity)[keyof typeof MessageSeverity];
    text: string;
}

export interface LotCustody {
    /** Null when nobody holds the lot — it is stranded back in the admin's queue. */
    holderName: string | null;
    /** The invoice the live line sits on. Null exactly when there is no holder. */
    invoiceNumber: string | null;
    /** Money owed back if the lot moved now, collected or still clearing. */
    heldCents: number;
    /**
     * The held money is a charge the processor has not confirmed. The refund is
     * sent on release either way, but the processor holds it against the charge
     * until that clears — so the sentence promises the refund without dating it.
     */
    chargeInFlight: boolean;
    /** The recipient of an offer standing on the lot, if one stands. */
    standingOfferName: string | null;
    /** What the sentences call the lot. Defaults to "item". */
    noun?: LotNoun;
}

/**
 * Both modes get these. Send-offer is NOT the money-free path: ADR-014 has an
 * admin offering a paid lot perform the refund at offer creation, so a dialog
 * that hid these in offer mode would move real money silently.
 *
 * Refund and release-only are exclusive readings of the same step — a lot
 * cannot both hold money and hold none — so at most two messages ever stack.
 */
export function consequenceMessages(lot: LotCustody): ConsequenceMessage[] {
    const messages: ConsequenceMessage[] = [];
    const noun = lot.noun ?? LotKind.item;

    if (lot.standingOfferName) {
        messages.push({
            kind: ConsequenceKind.EndsOffer,
            severity: MessageSeverity.Warn,
            text: `This ends the offer standing with ${lot.standingOfferName}.`,
        });
    }

    // No holder means no line to release and no money to send back. Saying
    // either of the sentences below would describe work that cannot happen.
    if (!lot.invoiceNumber || !lot.holderName) return messages;

    if (lot.heldCents > 0 && lot.chargeInFlight) {
        messages.push({
            kind: ConsequenceKind.RefundWhenSettled,
            severity: MessageSeverity.Warn,
            text: `${lot.holderName}'s payment for this ${noun} is still clearing. A refund of ${formatCents(lot.heldCents)} will be issued to them once it settles.`,
        });
    } else if (lot.heldCents > 0) {
        messages.push({
            kind: ConsequenceKind.RefundAndRelease,
            severity: MessageSeverity.Warn,
            text: `${lot.holderName} has already paid for this ${noun}. A refund of ${formatCents(lot.heldCents)} will be issued to them.`,
        });
    } else {
        messages.push({
            kind: ConsequenceKind.ReleaseOnly,
            severity: MessageSeverity.Info,
            text: `${lot.holderName} is released from this ${noun}. Nothing was collected, so no money moves.`,
        });
    }

    return messages;
}

/**
 * What would actually go back if the lot moved now. Gated on a payment having
 * reached the processor, not on what the line is worth: an unpaid line owes the
 * bidder nothing, however large the amount on it. A charge still clearing owes
 * it — the release books the obligation and the drain issues it on settlement.
 */
export function heldOnLineCents(line: {
    status: PaymentStatus;
    amountCents: number;
    refundedCents: number;
}): number {
    if (!releaseRefunds(line.status)) return 0;
    return Math.max(0, line.amountCents - line.refundedCents);
}

// ── Pricing a target who never bid ───────────────────────────────────────

// Which of the two applies is not inferable from the screen and it decides
// what the bidder is billed, so it is always said in words.
export const PricingBasis = {
    ValueAtClose: "the item's fair market value",
    LineAmount: 'the current line amount, as this item has no fair market value set',
} as const;
export type PricingBasisValue = (typeof PricingBasis)[keyof typeof PricingBasis];

// The same two readings for a bundle, which is priced as one lot.
export const BundlePricingBasis = {
    ValueAtClose: "the bundle's fair market value",
    LineAmount: 'the current line amount, as this bundle has no fair market value set',
} as const satisfies Record<keyof typeof PricingBasis, string>;
export type BundlePricingBasisValue = (typeof BundlePricingBasis)[keyof typeof BundlePricingBasis];

const hasRecordedValue = (fmvCents: number | null): boolean => fmvCents !== null && fmvCents > 0;

export function pricingBasis(
    fmvCents: number | null,
    noun: LotNoun = LotKind.item,
): PricingBasisValue | BundlePricingBasisValue {
    const basis = noun === LotKind.bundle ? BundlePricingBasis : PricingBasis;
    return hasRecordedValue(fmvCents) ? basis.ValueAtClose : basis.LineAmount;
}

// Mirrors the server's own fallback: a number on screen that differs from the
// number charged is a money lie.
export function nonBidderPriceCents(price: {
    fmvCents: number | null;
    lineAmountCents: number;
}): number {
    return hasRecordedValue(price.fmvCents) ? (price.fmvCents ?? 0) : price.lineAmountCents;
}

export function pricingNote(note: {
    firstName: string;
    amountCents: number;
    fmvCents: number | null;
    noun?: LotNoun;
}): string {
    const noun = note.noun ?? LotKind.item;
    return `${note.firstName} did not bid on this ${noun}. Reassigning at ${formatCents(note.amountCents)}, ${pricingBasis(note.fmvCents, noun)}.`;
}

export interface PlacementPreview {
    subject: ReassignSubject;
    mode: ReassignModeValue;
    name: string;
    firstName: string;
    priceCents: number;
    openInvoiceNumber: string | null;
    openInvoice: PlacementOpenInvoice | null;
    collect: PlacementCollect | null;
    cardLabel: string | null;
}

/**
 * The one sentence the dialog shows about the placement itself: who ends up
 * owing what. Anything about the previous holder is a separate warning, and
 * only when money moves back or an offer ends.
 */
export function placementMessage(preview: PlacementPreview): ConsequenceMessage {
    const noun = preview.subject.lotRef.kind;
    const price = formatCents(preview.priceCents);
    return { kind: ConsequenceKind.Placement, severity: MessageSeverity.Info, text: placementSentence(preview, noun, price) };
}

function placementSentence(preview: PlacementPreview, noun: LotNoun, price: string): string {
    if (preview.mode === ReassignMode.SendOffer) {
        return `${preview.firstName} will be offered this ${noun} for ${price}.`;
    }
    if (preview.collect !== CollectChoice.charge) {
        return `${preview.firstName} will owe ${price} for this ${noun} and get an invoice by email.`;
    }
    const card = `${preview.firstName}'s ${preview.cardLabel ?? 'card on file'}`;
    const invoice = preview.openInvoice;
    if (invoice && invoice.otherUnpaidLineCount > 0) {
        const total = formatCents(invoice.totalCents + preview.priceCents);
        const others = countLabel(invoice.otherUnpaidLineCount, 'other unpaid item');
        return `${card} will be charged ${total}: ${price} for this ${noun} and ${others}.`;
    }
    return `${card} will be charged ${price} for this ${noun}.`;
}

// ── Context ──────────────────────────────────────────────────────────────

const NOBODY_HOLDS_IT = "unclaimed. It's back in your queue";

export function contextLine(lot: {
    lot: string | null;
    itemTitle: string;
    holderName: string | null;
    amountCents: number;
    unsold?: boolean;
    hadBids?: boolean;
}): string {
    const named = [lot.lot, lot.itemTitle].filter(Boolean).join(' · ');
    if (lot.unsold) {
        const closed = lot.hadBids
            ? `Unsold at close. The high bid was ${formatCents(lot.amountCents)}`
            : 'Unsold at close. Nobody bid on it';
        return `${named} · ${closed}`;
    }
    return `${named} · Currently with ${lot.holderName ?? NOBODY_HOLDS_IT} at ${formatCents(lot.amountCents)}`;
}

// ── What the response says happened ──────────────────────────────────────

/**
 * The response wins over the preview. In `now` mode the server names the
 * invoice it actually used, so a placement that disagrees with the predicted
 * one reads as a minting — the surface must not show the prediction.
 *
 * `offer` mode creates no invoice, so there is nothing to compare and the
 * preview stands. That preview is the server's own answer, not a client guess.
 */
export function wasAppended(placement: {
    newInvoiceUuid: string | null;
    openInvoiceUuid: string | null;
}): boolean {
    if (placement.newInvoiceUuid === null) return placement.openInvoiceUuid !== null;
    return placement.newInvoiceUuid === placement.openInvoiceUuid;
}

export interface PlacementResult {
    mode: ReassignModeValue;
    name: string;
    appended: boolean;
    /** The invoice the lot landed on (`now`) or would land on (`offer`). */
    invoiceNumber: string | null;
    /** Measured from the deadline the server returned. `offer` mode only. */
    windowLabel?: string;
}

/**
 * Neither result sentence says where the obligation landed.
 *
 * Whether it appended to an invoice the bidder already had open or minted a new
 * one is bookkeeping the operator neither chose nor can change, and naming an
 * invoice invited them to go looking for it. What they actually need to know
 * after a lot moves is who has it and whether anything was charged.
 */

/**
 * Built from the response, never from the pre-flight guess. Both readings end
 * on what was NOT charged, because that is the question an admin asks first
 * after a lot moves. /* §5.1 what confirming does
 */
export function resultMessage(result: PlacementResult): string {
    if (result.mode === ReassignMode.SendOffer) {
        const window = result.windowLabel ?? OFFER_WINDOW_LABEL;
        return `Offer sent to ${result.name}, good for ${window}. They will not owe anything until they accept.`;
    }
    return `Reassigned to ${result.name}. Nothing was charged.`;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const HOURS_CEILING_MS = 2 * DAY_MS;

/**
 * The window the server will actually enforce, in the grammar of "good for X".
 * The wire returns the DB deadline precisely so the UI never promises a window
 * it will not honour; the product window stands in only when no deadline came
 * back at all.
 *
 * Rounded, NOT truncated — and that is the opposite of `expiresInLabel`, on
 * purpose. That one is a live countdown, where rounding up makes an admin
 * late. This one describes the length of a window created milliseconds ago, so
 * truncating reports the 24 hours the server just granted as 23.
 */
export function offerWindowLabel(expiresAt: string | null, now: Date = new Date()): string {
    if (!expiresAt) return OFFER_WINDOW_LABEL;
    const remainingMs = new Date(expiresAt).getTime() - now.getTime();
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) return OFFER_WINDOW_LABEL;
    if (remainingMs < HOUR_MS) return countLabel(Math.round(remainingMs / MINUTE_MS), 'minute');
    if (remainingMs < HOURS_CEILING_MS) return countLabel(Math.round(remainingMs / HOUR_MS), 'hour');
    return countLabel(Math.round(remainingMs / DAY_MS), 'day');
}
